import { usePathname } from 'next/navigation';
import styled from '@emotion/styled';
import { getCurrentAirportCodeFromSlug } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import { IParkingAvailability } from '../domain/models/IParkingAvailability';
import { getParkingAvailabilityPercentageData } from '../domain/utils/getParkingAvailabilityPercentageData';
import { CarIcon, Css, ITheme, Shimmer, TextButton } from '../../../ui';
import { getParkingAvailabilityIconColor } from '../domain/utils/getParkingAvailabilityIconColor';
import {
	InfoCard,
	InfoCardDescription,
	InfoCardDivider,
	InfoCardIcons,
	InfoCardRemark,
	InfoCardTitle,
	InfoCardTitleSection,
	InfoCardTop,
	InfoCardTopRightSection,
} from '../../../components/common/cards/InfoCard/InfoCard.styles';
import ParkingValet from '../../../assets/icons/parking/parking-valet.svg';
import { StyledParkingPriceShimmerDescription } from './ParkingCardsShimmer';
import { getParkingMapDirectionsUrl } from '../domain/utils/getParkingMapDirectionsUrl';

export interface IPoiPosition {
	floorId?: string;
	latitude: number;
	longitude: number;
}

interface IParkingCard {
	parking: IParkingAvailability;
	override: boolean;
	position?: IPoiPosition;
	price?: string;
	priceLoading?: boolean;
	priceError?: boolean;
}

export default function ParkingCard({
	parking,
	override,
	position,
	price,
	priceLoading,
	priceError,
}: IParkingCard) {
	const pathname = usePathname();
	const airportCode = getCurrentAirportCodeFromSlug(pathname);
	const { name, total, occupied, attributes, status } = parking;

	const hadOverrideStatus = override && !!status;

	const capacityData = getParkingAvailabilityPercentageData(
		total,
		occupied,
		airportCode,
		hadOverrideStatus,
	);

	return (
		<InfoCard>
			<InfoCardTop>
				<div>
					<InfoCardTitleSection>
						<InfoCardTitle>{name}</InfoCardTitle>
					</InfoCardTitleSection>
					<StyledInfoCardDescription>
						{priceLoading ? (
							<Shimmer>
								<StyledParkingPriceShimmerDescription />
							</Shimmer>
						) : priceError ? null : (
							price && <div>{price}/Day</div>
						)}
						{attributes.includes('Valet') && (
							<ParkingValetWrapper>
								<ParkingValet aria-hidden /> Valet upgrade available
							</ParkingValetWrapper>
						)}
					</StyledInfoCardDescription>
				</div>
				<InfoCardTopRightSection>
					<StyledInfoCardIcons aria-label={capacityData?.remark}>
						{[...Array(3)].map((_, index) => (
							<InfoCarIcon
								key={index} /* eslint-disable-line react/no-array-index-key */
								fill={getParkingAvailabilityIconColor(
									index,
									3,
									capacityData?.number,
								)}
							/>
						))}
					</StyledInfoCardIcons>
					{status && <InfoCardRemark>{status}</InfoCardRemark>}
				</InfoCardTopRightSection>
			</InfoCardTop>

			<InfoCardDivider />

			<GetDirectionsButton
				type="button"
				onClick={() => getParkingMapDirectionsUrl(airportCode, position)}
				aria-label={`Get directions to ${name}`}
			>
				Get directions
			</GetDirectionsButton>
		</InfoCard>
	);
}

const InfoCarIcon = styled(CarIcon)<{ fill: string }>`
	path {
		fill: ${({ fill }) => fill};
	}
`;

const StyledInfoCardIcons = styled(InfoCardIcons)`
	gap: 0;
`;

const StyledInfoCardDescription = styled(InfoCardDescription)`
	display: flex;
	flex-direction: row;
	gap: ${Css.pxToRem(16)};
`;

const ParkingValetWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${Css.pxToRem(4)};
`;

const GetDirectionsButton = styled(TextButton)<{}, ITheme>`
	color: ${(props) => props.theme.color.secondary};
	font-weight: 700;
	font-size: ${Css.pxToRem(16)};

	&:hover {
		color: ${(props) => props.theme.button.textButton.hover.font.color};
	}
`;
